/**
 * Пин крупно: оверлей поверх страницы. Esc закрывает, стрелки листают — но не
 * пока курсор в поле ввода: там стрелки двигают каретку.
 *
 * «В библиотеку» кладёт картинку пина (у видео-пина — обложку: mp4 в генерацию
 * кадра не отправить). Ссылка на Pinterest — только у настоящего пина, чей код
 * из цифр: у прочих кодов адреса на Pinterest нет.
 *
 * Блок «Теги» вешает и снимает теги этого пина теми же пакетными мутациями, что
 * панель выделения (пакет из одного); мутации даёт владелец. На экране подборки
 * — ещё «найден по словам» ссылками на экраны слов.
 */
import { useMutation } from "@tanstack/react-query";
import { Bookmark, ChevronLeft, ChevronRight, ExternalLink, Library, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";

import { LibraryPicker } from "@/components/library/LibraryPicker";
import { ErrorLine, input } from "@/components/trends/ui";
import { mediaUrl, num } from "@/lib/media";
import { tagStyle, type Tag, type TagBy } from "@/lib/tags";
import { ageText, durationText, pinterestUrl, post, ruDate, trendText, type Pin } from "@/lib/trends";

export function PinViewer({
  pins,
  index,
  onIndex,
  onClose,
  defaultLibraryName,
  lastLibraryId,
  onAdded,
  tags,
  onTag,
  onUntag,
  wordNames,
  fittingTo,
}: {
  pins: Pin[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  defaultLibraryName: string;
  lastLibraryId: number | null;
  /** Пин лёг в библиотеку: владелец обновляет `in_libraries` и запоминает выбор. */
  onAdded: (libraryId: number) => void;
  tags: Tag[];
  onTag: (pinIds: number[], by: TagBy) => Promise<void>;
  onUntag: (pinIds: number[], tagId: number) => Promise<void>;
  /** Экран подборки: слова, нашедшие пин. */
  wordNames?: ReadonlyMap<number, string>;
  /** Адрес примерки показанного пина. */
  fittingTo?: string;
}) {
  const pin = pins[index];
  const count = pins.length;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA")) return;
      if (event.key === "ArrowRight" && index < count - 1) onIndex(index + 1);
      if (event.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, count, onIndex, onClose]);

  if (!pin) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch bg-black/75 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Пин крупно"
      onClick={onClose}
      data-annot="пин крупно"
    >
      <button
        type="button"
        className="absolute top-3 right-[352px] z-10 grid size-9 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
        onClick={onClose}
        title="Закрыть (Esc)"
      >
        <X size={18} />
      </button>

      <div className="flex min-w-0 flex-1 items-center justify-center gap-2 p-4">
        <NavButton dir="prev" disabled={index === 0} onClick={() => onIndex(index - 1)} />
        <div className="flex h-full min-w-0 flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <PinPicture key={pin.id} pin={pin} />
        </div>
        <NavButton dir="next" disabled={index >= count - 1} onClick={() => onIndex(index + 1)} />
      </div>

      <aside
        className="flex w-[340px] flex-none flex-col gap-3 overflow-y-auto border-l border-border bg-bg p-4 text-[12.5px]"
        onClick={(e) => e.stopPropagation()}
      >
        <PinInfo
          key={pin.id}
          pin={pin}
          position={`${index + 1} из ${count}`}
          defaultLibraryName={defaultLibraryName}
          lastLibraryId={lastLibraryId}
          onAdded={onAdded}
          tags={tags}
          onTag={onTag}
          onUntag={onUntag}
          {...(wordNames ? { wordNames } : {})}
          {...(fittingTo ? { fittingTo } : {})}
        />
      </aside>
    </div>
  );
}

function NavButton({ dir, disabled, onClick }: { dir: "prev" | "next"; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={dir === "prev" ? "Предыдущий (←)" : "Следующий (→)"}
      className="grid size-10 flex-none place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:invisible"
    >
      {dir === "prev" ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
    </button>
  );
}

export function PinPicture({ pin }: { pin: Pin }) {
  const cls = "max-h-[calc(100vh-2rem)] max-w-full rounded-[var(--radius-card)] object-contain shadow-2xl";
  // Оригинал скачан только у «лучших» сбора; у прочих — превью 474px.
  const picture = pin.asset_path ?? pin.thumb_path;
  if (pin.kind === "video" && pin.video_path)
    return (
      <video
        src={mediaUrl(pin.video_path)}
        poster={mediaUrl(picture)}
        className={cls}
        controls
        autoPlay
        muted
        loop
        playsInline
      />
    );
  if (!picture) return <div className="text-[13px] text-white/70">Картинка пина не скачана.</div>;
  return <img src={mediaUrl(picture)} alt={pin.title ?? ""} className={cls} />;
}

function Metric({ term, value, hint }: { term: string; value: string; hint?: string }) {
  return (
    <>
      <dt className="text-fg-faint" title={hint}>
        {term}
      </dt>
      <dd className="tnum text-fg">{value}</dd>
    </>
  );
}

export function PinInfo({
  pin,
  position,
  defaultLibraryName,
  lastLibraryId,
  onAdded,
  tags,
  onTag,
  onUntag,
  wordNames,
  fittingTo,
}: {
  pin: Pin;
  position?: string;
  defaultLibraryName: string;
  lastLibraryId: number | null;
  onAdded: (libraryId: number) => void;
  tags: Tag[];
  onTag: (pinIds: number[], by: TagBy) => Promise<void>;
  onUntag: (pinIds: number[], tagId: number) => Promise<void>;
  wordNames?: ReadonlyMap<number, string>;
  /** Адрес примерки этого пина; без него ссылки «Генерировать» нет. */
  fittingTo?: string;
}) {
  const [done, setDone] = useState("");
  const pinUrl = pinterestUrl(pin.code);

  const add = useMutation({
    mutationFn: ({ libraryId }: { libraryId: number; name: string }) =>
      post<{ reference_item_id: number }>(`/api/pins/${pin.id}/to-library`, { library_id: libraryId }),
    onSuccess: (_got, { libraryId, name }) => {
      setDone(`Лежит в «${name}».`);
      onAdded(libraryId);
    },
  });

  return (
    <>
      {position ? <div className="text-[11.5px] text-fg-faint">{position} · ← → листать, Esc — закрыть</div> : null}
      <h2 className="text-[14px] font-medium leading-snug text-fg">
        {pin.title || <span className="text-fg-faint">без заголовка</span>}
      </h2>
      {wordNames && pin.type_ids?.length ? (
        <div className="text-[12px] text-fg-muted" data-annot="найден по словам">
          найден по словам:{" "}
          {pin.type_ids.map((id, i) => (
            <span key={id}>
              {i ? ", " : ""}
              <Link to={`/trends/type/${id}`} className="text-accent-text hover:underline">
                {wordNames.get(id) ?? `#${id}`}
              </Link>
            </span>
          ))}
        </div>
      ) : null}

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-fg-faint" title="Сохранения картинки — все её копии на Pinterest">
          сохранений
        </dt>
        <dd className="inline-flex items-center gap-1 tnum text-fg">
          <Bookmark size={11} />
          {num(pin.saves)}
        </dd>
        <Metric term="лайков" value={num(pin.reactions)} hint="Лайки картинки — все её копии" />
        {pin.date_detail ? (
          <>
            <Metric term="репинов" value={num(pin.repins)} hint="Сохранения именно этой копии" />
            <Metric term="пересылок" value={num(pin.shares)} />
            <Metric term="комментариев" value={num(pin.comments)} />
            <Metric
              term="скорость"
              value={pin.speed !== null ? `${pin.speed.toLocaleString("ru-RU")} реп./день · ${trendText(pin.trend)}` : "—"}
              hint="Репинов этой копии в день с публикации (возраст не меньше недели) и во сколько раз это быстрее медианы среза (слова или подборки) среди пинов того же вида (картинки / видео)"
            />
          </>
        ) : (
          <>
            <dt className="text-fg-faint">репины</dt>
            <dd className="text-fg-faint">карточку не снимали — пин не в «лучших» сбора</dd>
          </>
        )}
        <dt className="text-fg-faint">вид</dt>
        <dd className="text-fg-muted">
          {pin.kind === "video"
            ? `${pin.video_path ? "видео" : "видео (ролик не скачан, только обложка)"}${pin.video_ms ? ` · ${durationText(pin.video_ms)}` : ""}`
            : "картинка"}
        </dd>
        {pin.origin ? (
          <>
            <dt className="text-fg-faint">пин</dt>
            <dd className="text-fg-muted">
              {pin.origin === "repin" ? "репин чужой картинки — дата у копии, картинка старше" : "свой пин автора"}
            </dd>
          </>
        ) : null}
        {pin.followers !== null ? (
          <Metric term="у автора" value={`${num(pin.followers)} подп.`} hint="Подписчиков у того, кто прикрепил пин" />
        ) : null}
        {pin.domain ? (
          <>
            <dt className="text-fg-faint">источник</dt>
            <dd className="truncate text-fg-muted" title={pin.domain}>
              {pin.domain}
            </dd>
          </>
        ) : null}
        {pin.date_publish ? (
          <>
            <dt className="text-fg-faint">опубликован</dt>
            <dd className="tnum text-fg-muted">
              {ruDate(pin.date_publish)} · {ageText(pin.age_days)}
            </dd>
          </>
        ) : null}
        {pin.asset_width && pin.asset_height ? (
          <>
            <dt className="text-fg-faint">размер</dt>
            <dd className="tnum text-fg-muted">
              {pin.asset_width}×{pin.asset_height}
            </dd>
          </>
        ) : null}
        {pin.in_libraries > 0 ? (
          <>
            <dt className="text-fg-faint">в библиотеках</dt>
            <dd className="inline-flex items-center gap-1 text-accent-text">
              <Library size={11} />×{pin.in_libraries}
            </dd>
          </>
        ) : null}
      </dl>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {pin.link ? (
          <a
            href={pin.link}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
          >
            <ExternalLink size={12} />
            источник
          </a>
        ) : null}
        {pinUrl ? (
          <a
            href={pinUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
          >
            <ExternalLink size={12} />
            на Pinterest
          </a>
        ) : null}
        <Link to={`/pins/${pin.id}`} className="inline-flex items-center gap-1 text-accent-text hover:underline">
          карточка пина
        </Link>
        {fittingTo ? (
          <Link to={fittingTo} className="inline-flex items-center gap-1 text-accent-text hover:underline">
            Генерировать
          </Link>
        ) : null}
      </div>

      <TagBlock pin={pin} tags={tags} onTag={onTag} onUntag={onUntag} />

      <div className="grid gap-2 border-t border-border pt-3" data-annot="в библиотеку">
        <div className="text-[12.5px] font-medium text-fg">В библиотеку референсов</div>
        {!pin.asset_path && !pin.thumb_path ? (
          <div className="text-fg-faint">Картинка пина не скачана — класть нечего.</div>
        ) : (
          <>
            {pin.kind === "video" ? (
              <div className="text-[11.5px] text-fg-faint">У видео в библиотеку ляжет обложка.</div>
            ) : null}
            {!pin.asset_path ? (
              <div className="text-[11.5px] text-fg-faint">Оригинал скачается с Pinterest при добавлении.</div>
            ) : null}
            <LibraryPicker
              initialLibraryId={lastLibraryId}
              defaultName={defaultLibraryName}
              busy={add.isPending}
              onPick={(libraryId, name) => add.mutateAsync({ libraryId, name }).then(() => undefined, () => undefined)}
            />
            {done ? (
              <div className="rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1.5 text-ok">{done}</div>
            ) : null}
            <ErrorLine error={add.error} />
          </>
        )}
      </div>
    </>
  );
}

/** Теги пина: фишки с «×» и поле-комбобокс — найти существующий или завести новый. */
function TagBlock({
  pin,
  tags,
  onTag,
  onUntag,
}: {
  pin: Pin;
  tags: Tag[];
  onTag: (pinIds: number[], by: TagBy) => Promise<void>;
  onUntag: (pinIds: number[], tagId: number) => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const names = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags]);
  const needle = query.trim().toLowerCase();
  const options = useMemo(
    () => (needle ? tags.filter((t) => !pin.tag_ids.includes(t.id) && t.name.toLowerCase().includes(needle)) : []),
    [tags, pin.tag_ids, needle],
  );
  const exact = tags.find((t) => t.name.toLowerCase() === needle);

  const act = async (job: Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await job;
      setQuery("");
    } catch (exc) {
      setError(exc);
    } finally {
      setBusy(false);
    }
  };
  const add = (by: TagBy) => void act(onTag([pin.id], by));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !needle) return;
    if (exact) {
      if (!pin.tag_ids.includes(exact.id)) add({ tagId: exact.id });
      else setQuery("");
    } else if (options.length === 1) add({ tagId: options[0]!.id });
    else add({ name: query.trim().split(/\s+/).join(" ") });
  };

  return (
    <div className="grid gap-2 border-t border-border pt-3" data-annot="теги пина">
      <div className="text-[12.5px] font-medium text-fg">Теги</div>
      {pin.tag_ids.length ? (
        <div className="flex flex-wrap gap-1">
          {pin.tag_ids.map((id) => (
            <span
              key={id}
              className="inline-flex max-w-full items-center gap-0.5 rounded-[3px] pl-1.5 text-[11.5px]"
              style={tagStyle(id)}
            >
              <span className="truncate">{names.get(id) ?? `#${id}`}</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => void act(onUntag([pin.id], id))}
                className="grid size-5 place-items-center rounded-[3px] hover:bg-black/25"
                title="Снять тег"
                aria-label={`Снять тег ${names.get(id) ?? id}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[11.5px] text-fg-faint">Тегов нет.</div>
      )}
      <form onSubmit={submit} className="relative">
        <input
          className={`${input} w-full`}
          value={query}
          maxLength={60}
          disabled={busy}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="добавить тег… ⏎"
        />
        {needle ? (
          <div className="absolute top-full right-0 left-0 z-10 mt-1 grid max-h-[200px] overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg p-1 shadow-xl">
            {options.map((t) => (
              <button
                key={t.id}
                type="button"
                disabled={busy}
                onClick={() => add({ tagId: t.id })}
                className="flex items-center rounded-[var(--radius-ui)] px-1.5 py-1 text-left hover:bg-bg-hover"
              >
                <span className="truncate rounded-[3px] px-1 text-[11.5px]" style={tagStyle(t.id)}>
                  {t.name}
                </span>
              </button>
            ))}
            {!exact ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => add({ name: query.trim().split(/\s+/).join(" ") })}
                className="flex items-center gap-1 rounded-[var(--radius-ui)] px-1.5 py-1 text-left text-[12px] text-accent-text hover:bg-bg-hover"
              >
                <Plus size={12} />
                создать «{query.trim()}»
              </button>
            ) : pin.tag_ids.includes(exact.id) ? (
              <div className="px-1.5 py-1 text-[12px] text-fg-faint">«{exact.name}» уже на пине.</div>
            ) : null}
          </div>
        ) : null}
      </form>
      <ErrorLine error={error} />
    </div>
  );
}
